import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DnsIcon from '@mui/icons-material/Dns';
import {
  disableEventCustomDomain,
  getEventCustomDomain,
  saveEventCustomDomain,
  verifyEventCustomDomain,
  type EventCustomDomain,
} from '../../services/customDomainService';

interface Props {
  eventId: string;
  eventTitle: string;
}

const statusMeta: Record<
  string,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  pending: { label: 'Aguardando DNS', color: 'warning' },
  active: { label: 'Ativo', color: 'success' },
  disabled: { label: 'Desativado', color: 'default' },
};

const EventWorkspaceCustomDomainTab: React.FC<Props> = ({ eventId, eventTitle }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [data, setData] = useState<EventCustomDomain | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const current = await getEventCustomDomain(eventId);
      setData(current);
      if (current?.domain) setDomainInput(current.domain);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar domínio.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await saveEventCustomDomain(eventId, domainInput);
      setData(saved);
      setSuccess('Domínio salvo. Configure o CNAME e clique em Verificar DNS.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar domínio.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const verified = await verifyEventCustomDomain(eventId);
      setData(verified);
      setSuccess('Domínio verificado e ativo!');
    } catch (err: any) {
      const payload = err?.response?.data;
      if (payload?.data) setData(payload.data);
      setError(payload?.message || 'DNS ainda não está correto.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Desativar o domínio personalizado deste evento?')) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const disabled = await disableEventCustomDomain(eventId);
      setData(disabled);
      setSuccess('Domínio desativado.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao desativar.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copiado!');
    } catch {
      setError('Não foi possível copiar.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const meta = data ? statusMeta[data.status] : null;

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <DnsIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Domínio personalizado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Publique “{eventTitle}” no domínio da sua organização (ex.: congresso.suaigreja.org).
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {data && meta && (
          <Chip size="small" color={meta.color} label={meta.label} sx={{ alignSelf: 'flex-start' }} />
        )}

        <TextField
          label="Domínio"
          placeholder="congresso.suaigreja.org"
          value={domainInput}
          onChange={(e) => setDomainInput(e.target.value)}
          fullWidth
          helperText="Sem http://. Preferencialmente um subdomínio (CNAME)."
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" onClick={handleSave} disabled={busy || !domainInput.trim()}>
            Salvar domínio
          </Button>
          <Button variant="outlined" onClick={handleVerify} disabled={busy || !data}>
            Verificar DNS
          </Button>
          {data && data.status !== 'disabled' && (
            <Button color="inherit" onClick={handleDisable} disabled={busy}>
              Desativar
            </Button>
          )}
        </Stack>

        {data?.dns && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Configure no seu DNS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {data.dns.instructions}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 72 }}>
                  Tipo
                </Typography>
                <Chip size="small" label={data.dns.type} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 72 }}>
                  Host
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {data.dns.host}
                </Typography>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopy(data.dns.host)}>
                  Copiar
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 72 }}>
                  Valor
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {data.dns.value}
                </Typography>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopy(data.dns.value)}>
                  Copiar
                </Button>
              </Stack>
            </Stack>
            {data.lastError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {data.lastError}
              </Alert>
            )}
          </Paper>
        )}

        {data?.status === 'active' && data.publicUrl && (
          <Alert
            severity="success"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={data.publicUrl}
                target="_blank"
                rel="noreferrer"
              >
                Abrir
              </Button>
            }
          >
            Página ativa em {data.publicUrl}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default EventWorkspaceCustomDomainTab;
